/**
 * RBAC page catalog — keep in sync with supabase/functions/_shared/rbacPageKeys.ts
 * and seeds in migration 015_rbac_roles_permissions.sql.
 */

/** Legacy matrix key; UI resolves to `clients` for access checks. */
export const LEGACY_PAGE_KEY_CLIENTS = 'dashboard'

export const PAGE_CATALOG = [
  { key: 'platform-overview', label: 'Dashboard (platform overview)' },
  { key: 'clients', label: 'Client Dashboard' },
  { key: 'clients:table',    label: 'Table',            parentKey: 'clients' },
  { key: 'clients:pipeline', label: 'Pipeline',          parentKey: 'clients' },
  { key: 'clients:firewall', label: 'Firewalls',         parentKey: 'clients' },
  { key: 'clients:activity', label: 'Activity',          parentKey: 'clients' },
  { key: 'clients:settings', label: 'Settings',          parentKey: 'clients' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'elections', label: 'Election Data' },
  { key: 'primary-calendar', label: 'Primary Calendar' },
  { key: 'adimpact-alerts', label: 'Client AdImpact Alerts' },
  { key: 'big-political-list', label: 'Big Political List' },
  { key: 'big-political-list:stations',     label: 'Big Political List',  parentKey: 'big-political-list' },
  { key: 'big-political-list:managers',     label: 'Rep Firm Managers',   parentKey: 'big-political-list' },
  { key: 'big-political-list:station-share',label: 'Station Share',       parentKey: 'big-political-list' },
  { key: 'big-political-list:changelog',    label: 'Change Log',          parentKey: 'big-political-list' },
  { key: 'big-political-list:import',       label: 'Import',              parentKey: 'big-political-list' },
  { key: 'resources', label: 'Resources' },
  { key: 'race-tracker', label: 'Race Tracker' },
  { key: 'race-tracker:dashboard',   label: 'Client Dashboard', parentKey: 'race-tracker' },
  { key: 'race-tracker:list',        label: 'List View',        parentKey: 'race-tracker' },
  { key: 'race-tracker:analytics',   label: 'Analytics',        parentKey: 'race-tracker' },
  { key: 'race-tracker:intel-sweep', label: 'Intel Sweep',      parentKey: 'race-tracker' },
  { key: 'race-tracker:import',      label: 'Import',           parentKey: 'race-tracker' },
  { key: 'race-tracker:settings',    label: 'Settings',         parentKey: 'race-tracker' },
  { key: 'idea-board', label: 'Idea Board' },
  { key: 'idea-board:board', label: 'Idea Board', parentKey: 'idea-board' },
  { key: 'idea-board:admin', label: 'Idea Board Admin', parentKey: 'idea-board' },
  { key: 'ad-ops-hq', label: 'Ad Ops HQ' },
  { key: 'rate-auditing', label: 'Rate Auditing' },
  { key: 'command-center', label: 'Command Center' },
  { key: 'settings-users', label: 'User Management (settings)' },
  { key: 'settings-integrations', label: 'Integrations (settings)' },
  { key: 'basecamp-api-references', label: 'Basecamp API (legacy)', hiddenInMatrix: true },
  { key: 'technical-documentation', label: 'App documentation' },
  { key: 'basecamp-knowledge', label: 'Basecamp Knowledge Base' },
  { key: 'basecamp-projects', label: 'Basecamp Project AI Insights' },
  { key: 'ai-agents', label: 'AI — Agents Setting' },
  { key: 'ai-agents-logs', label: 'AI — Agents Logs' },
  { key: 'ai-cost-monitoring', label: 'AI — Cost and Monitoring' },
  { key: 'outlook', label: 'Outlook — Email Read & Summarize' },
  { key: 'sharepoints', label: 'SharePoint' },
  { key: 'onenotes', label: 'OneNote' },
  // Main-shell /knowledge (search + assistant) — separate from admin KB tabs below
  { key: 'knowledge-search', label: 'Knowledge Search (/knowledge)' },
  { key: 'knowledge-base', label: 'Knowledge Base (admin)' },
  { key: 'knowledge-base:dashboard', label: 'Overview', parentKey: 'knowledge-base' },
  { key: 'knowledge-base:content', label: 'Library', parentKey: 'knowledge-base' },
  { key: 'knowledge-base:wiki', label: 'Wiki', parentKey: 'knowledge-base' },
  { key: 'knowledge-base:access', label: 'Access (incl. Developer Tools)', parentKey: 'knowledge-base' },
  { key: 'mcp-server:overview', label: 'Overview', parentKey: 'mcp-server' },
  { key: 'mcp-server:keys', label: 'API Keys', parentKey: 'mcp-server' },
  { key: 'mcp-server:tools', label: 'Tools', parentKey: 'mcp-server' },
  { key: 'mcp-server:logs', label: 'Logs', parentKey: 'mcp-server' },
]

/** Pages shown in the role permissions matrix (excludes legacy/hidden keys still in RBAC). */
export const PERMISSIONS_MATRIX_PAGES = PAGE_CATALOG.filter((p) => !p.hiddenInMatrix)

/** Page keys that grant /knowledge — keep in sync with KB_SEARCH_PAGE_KEYS in kbCatalog.ts */
export const KNOWLEDGE_SEARCH_PAGE_KEYS = [
  'knowledge-search',
  'knowledge-base',
  'knowledge-base:dashboard',
  'knowledge-base:content',
  'knowledge-base:access',
  'knowledge-base:wiki',
]

/** @typedef {{ page_key: string; can_access: boolean; access_level: 'read' | 'write' }} RolePagePermissionRow */

/**
 * Normalize nav / legacy keys to matrix page_key.
 * @param {string} pageKey
 * @returns {string}
 */
export function resolvePageKey(pageKey) {
  if (pageKey === LEGACY_PAGE_KEY_CLIENTS) return 'clients'
  return pageKey
}

/**
 * @param {string} pageKey
 * @param {Map<string, RolePagePermissionRow>|null} byPage
 * @param {boolean} isSuperAdmin
 * @returns {boolean}
 */
export function canAccessPage(pageKey, byPage, isSuperAdmin) {
  if (isSuperAdmin) return true
  if (pageKey === 'platform-overview') {
    for (const key of ['platform-overview', 'clients', LEGACY_PAGE_KEY_CLIENTS]) {
      const row = byPage?.get(key)
      if (row?.can_access === true) return true
    }
    return false
  }
  const resolved = resolvePageKey(pageKey)
  const row = byPage?.get(resolved) ?? (resolved !== pageKey ? byPage?.get(pageKey) : undefined)
  return row?.can_access === true
}

/**
 * @param {Map<string, RolePagePermissionRow>|null} byPage
 * @param {boolean} isSuperAdmin
 * @returns {boolean}
 */
export function canAccessKnowledgeSearch(byPage, isSuperAdmin) {
  if (isSuperAdmin) return true
  return KNOWLEDGE_SEARCH_PAGE_KEYS.some((k) => byPage?.get(k)?.can_access === true)
}

/**
 * @param {Map<string, RolePagePermissionRow>|null} byPage
 * @param {boolean} isSuperAdmin
 * @returns {'none' | 'read' | 'write'}
 */
export function knowledgeSearchAccessLevel(byPage, isSuperAdmin) {
  if (isSuperAdmin) return 'write'
  let best = 'none'
  const order = { none: 0, read: 1, write: 2 }
  for (const key of KNOWLEDGE_SEARCH_PAGE_KEYS) {
    const row = byPage?.get(key)
    if (!row?.can_access) continue
    const level = row.access_level === 'read' ? 'read' : 'write'
    if (order[level] > order[best]) best = level
  }
  return best
}

/**
 * Returns true if the user can access a specific sub-tab within a page.
 * Falls back to true if no row exists for the tab key (graceful degradation
 * for environments that haven't run the tab-level RBAC migration yet).
 *
 * @param {string} parentKey  e.g. 'clients'
 * @param {string} tabId      e.g. 'table'
 * @param {Map<string, RolePagePermissionRow>|null} byPage
 * @param {boolean} isSuperAdmin
 * @returns {boolean}
 */
export function canAccessTab(parentKey, tabId, byPage, isSuperAdmin) {
  if (isSuperAdmin) return true
  const tabKey = `${parentKey}:${tabId}`
  if (!byPage?.has(tabKey)) return true
  return canAccessPage(tabKey, byPage, isSuperAdmin)
}

/**
 * @param {string} pageKey
 * @param {Map<string, RolePagePermissionRow>|null} byPage
 * @param {boolean} isSuperAdmin
 * @returns {'none' | 'read' | 'write'}
 */
export function pageAccessLevel(pageKey, byPage, isSuperAdmin) {
  if (isSuperAdmin) return 'write'
  const resolved = resolvePageKey(pageKey)
  const row = byPage?.get(resolved) ?? (resolved !== pageKey ? byPage?.get(pageKey) : undefined)
  if (!row?.can_access) return 'none'
  return row.access_level === 'read' ? 'read' : 'write'
}

/**
 * @param {'read' | 'write'} minLevel
 * @param {'none' | 'read' | 'write'} level
 */
export function meetsMinLevel(minLevel, level) {
  const order = { none: 0, read: 1, write: 2 }
  return order[level] >= order[minLevel]
}

/**
 * Count navigable modules the user can access (for overview hero).
 * @param {(key: string) => boolean} canAccessPageFn
 * @returns {number}
 */
export function countAccessibleModules(canAccessPageFn) {
  const keys = new Set([
    'platform-overview',
    'clients',
    'analytics',
    'elections',
    'adimpact-alerts',
    'big-political-list',
    'resources',
    'race-tracker',
    'idea-board',
    'knowledge-search',
    'ad-ops-hq',
    'rate-auditing',
    'command-center',
    'settings-users',
    'settings-integrations',
    'basecamp-api-references',
    'technical-documentation',
    'basecamp-knowledge',
    'basecamp-projects',
    'ai-agents',
    'ai-agents-logs',
    'ai-cost-monitoring',
    'outlook',
    'sharepoints',
    'onenotes',
  ])
  let n = 0
  for (const key of keys) {
    if (canAccessPageFn(key)) n += 1
  }
  return n
}
